import { lastValueFrom } from "rxjs";
import { createEffect, createResource, createSignal, For } from "solid-js";

import { DatesEditor } from "./DatesEditor";
import type { UtdoDate } from "../types";
import { action } from "../util/action";
import { useApi } from "../util/api";
import { formatDate } from "../util/format-date";
import { panic } from "../util/panic";
import { success } from "../util/success";

export function Dates() {
    const api = useApi();
    const [ currentId, setCurrentId ] = createSignal<number | undefined>(undefined);
    const [ dates, { refetch } ] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectDates());
            } catch (err: any) {
                panic(err);
            }
            return [] as UtdoDate[];
        },
        { initialValue: [] as UtdoDate[] }
    );

    function currentDate(): UtdoDate {
        const id = currentId();
        return dates().find((d) => d.id === id) ?? {
            id: 0,
            name: "",
            description: "",
            date: "",
            location: "",
            street: "",
            zip: "",
            city: "",
            country: "",
            cost: "",
            hidden: false,
            canceled: false,
        }
    }

    let dialogRef!: HTMLDialogElement;

    createEffect(() => {
        const id = currentId();
        if (id === undefined) {
            dialogRef.close();
            return;
        }
        dialogRef.showModal();
    });

    const editDate = (id?: number) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setCurrentId(id);
    };
    const save = async (date: UtdoDate) => {
        try {
            if ((date.id ?? 0) > 0) {
                if (await lastValueFrom(api.updateDate(date))) {
                    success("Date enregistrée avec succès.");
                }
            } else {
                if (await lastValueFrom(api.insertDate(date))) {
                    success("Date enregistrée avec succès.");
                }
            }
            setCurrentId(undefined);
            refetch();
        } catch (err: any) {
            panic(err);
        }
    }

    const deleteDate = action(
        async (d: UtdoDate) => {
            if (!confirm(`Vous allez supprimer la vidéo "${d.name}".\nContinuer ?`)) {
                return;
            }
            await lastValueFrom(api.deleteDate(d.id!));
        },
        refetch,
    )
    const toggleVisibility = action(
        async (d: UtdoDate) => await lastValueFrom(api.toggleDateVisibility(d.id!)),
        refetch,
    );

    return <section id="admin-dates">
        <h2>Dates</h2>
        <div>
            <div class="actions">
                <button onClick={editDate(0)}>Nouvelle date</button>
            </div>
            <ul>
                <For each={dates()}>
                    {(d) => <li>
                        <span>{formatDate(d.date)}</span>
                        <a href="#" onClick={editDate(d.id!)}>{d.name}{d.canceled ? " ANNULÉ" : ""}</a>
                        <div class="actions">
                            <a href="#"
                                class="icon"
                                onClick={toggleVisibility(d)}>{d.hidden ? "visibility_off" : "visibility"}</a>
                            <a href="#"
                                class="icon"
                                onClick={deleteDate(d)}>delete</a>
                        </div>
                    </li>}
                </For>
            </ul>
        </div>
        <dialog ref={dialogRef}>
            <DatesEditor
                date={currentDate()}
                validate={save}
                cancel={() => setCurrentId(undefined)}
                />
        </dialog>
    </section>;
}